import { NewApiService } from 'lib/services/api/newapi.service';
import { Observable, take, tap } from 'rxjs';
import { AjaxResponse } from 'rxjs/ajax';
import { BulkSaveResponse, LocalStorageQueueItem, TimerConfig } from '../interfaces/assessment-update-queue.interface';

export class AssessmentUpdateQueue {

  /**
   * Key for storing the queue in localStorage.
   */
  readonly key: string = 'apiQueue';

  /**
   * Timer configuration for processing the queue.
   */
  readonly timer: TimerConfig = {
    interval: 10000,
    id: null
  };

  /**
   * Initializes the queue in localStorage if it doesn't exist.
   */
  constructor(private readonly apiService: NewApiService) {
    if (!localStorage.getItem(this.key)) {
      localStorage.setItem(this.key, JSON.stringify([]));
    }
  }

  /**
   * Retrieves the current queue from localStorage.
   * Returns an empty array if the queue doesn't exist.
   */
  getQueue(): LocalStorageQueueItem[] {
    const queue = localStorage.getItem(this.key);
    return queue ? JSON.parse(queue) as LocalStorageQueueItem[] : [];
  }

  /**
   * Returns the index of an item in the queue by matching QuestionID, -1 otherwise.
   */
  findInQueue(questionId: number): number {
    return this.getQueue().findIndex(item => item.QuestionID === questionId);
  }

  /**
   * Adds a new item to the queue or updates an existing one.
   */
  enqueue(item: LocalStorageQueueItem): void {
    const queue = this.getQueue();
    const index = this.findInQueue(item.QuestionID);

    if (index !== -1) {
      queue[index] = item;
    } else {
      queue.push(item);
    }

    localStorage.setItem(this.key, JSON.stringify(queue));
  }

  /**
   * Removes and returns the first matching item by question ID.
   * Returns null if no match is found.
   */
  dequeue(questionId: number): LocalStorageQueueItem | null {
    const queue = this.getQueue();
    const index = this.findInQueue(questionId);

    if (index === -1) return null;

    const [removedItem] = queue.splice(index, 1);
    localStorage.setItem(this.key, JSON.stringify(queue));

    return removedItem;
  }

  /**
   * Sends the queue to the API and removes successfully processed items.
   */
  postQueue(): Observable<AjaxResponse<BulkSaveResponse>> {
    return this.apiService.post<BulkSaveResponse>({
      url: '/Assessment/BulkSave',
      data: this.getQueue(),
    }).pipe(
      tap(response => {
        const questionIdsToDequeue: number[] = Object.entries(response.response)
          .filter(([_, statusCode]) => statusCode === 200)
          .map(([id]) => +id);

        questionIdsToDequeue.forEach(id => this.dequeue(id));
      })
    );
  }

  /**
   * Starts processing the queue at regular intervals by sending items to the API.
   */
  start(): void {
    if (this.timer.id) return;

    this.timer.id = setInterval(() => {
      if (navigator.onLine && this.getQueue().length > 0) {
        this.postQueue().pipe(take(1)).subscribe();
      }
    }, this.timer.interval);
  }

  /**
   * Stops queue processing.
   */
  stop(): void {
    if (this.timer.id) {
      clearInterval(this.timer.id);
      this.timer.id = null;
    }
  }
}
